import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Press } from '@dorixona/ui'
import { Icon } from '../../ui/Icon'
import { Flag } from '../../ui/Flag'
import { failureText } from '../../ui/Load'
import { useSession } from '../../app/session'
import { useLanguage } from '../../app/language'
import type { Customer } from '../auth/api'
import { isNativeShell } from '../../app/nativeBridge'
import { LanguageSheet } from './LanguageSheet'
import { SecuritySettings } from './SecuritySettings'
import { useSaveProfile } from './api'

type Channel = 'notifyPush'

/**
 * Настройки кабинета: язык, уведомления и — в нашем Flutter-приложении — PIN-код.
 * Уведомления и PIN-код — только у вошедшего.
 */
export function SettingsPanel() {
  const { t } = useTranslation(['account', 'common'])
  const { customer } = useSession()
  const { language } = useLanguage()
  const [choosing, setChoosing] = useState(false)

  return (
    <>
      <div className="acc-sec">
        <Press
          squish="row"
          className="acc-pick"
          aria-haspopup="dialog"
          onPress={() => {
            setChoosing(true)
          }}
        >
          <span className="t1">{t('common:language.label')}</span>
          <span className="acc-pick__val">
            <Flag code={language} />
            {t(`common:language.${language}`, { lng: language })}
          </span>
          <Icon name="i-chev" size={16} />
        </Press>
      </div>
      <LanguageSheet
        opened={choosing}
        onClose={() => {
          setChoosing(false)
        }}
      />
      {customer ? <NotifySettings customer={customer} /> : null}
      {customer && isNativeShell() ? <SecuritySettings customerId={customer.id} /> : null}
    </>
  )
}

/**
 * Переключатель сохраняется сразу — отдельная кнопка «Сохранить» у одного
 * тумблера была бы лишним шагом. Сервер требует имя и пол в каждом PATCH,
 * поэтому отправляем их из профиля как есть. СМС не настраиваются: ими
 * приходит только код входа (R-116).
 */
function NotifySettings({ customer }: { customer: Customer }) {
  const { t } = useTranslation(['account', 'common'])
  const { language } = useLanguage()
  const save = useSaveProfile()
  const [notify, setNotify] = useState({ notifyPush: customer.notifyPush })
  const [message, setMessage] = useState('')

  const toggle = (channel: Channel, on: boolean) => {
    const before = notify
    const next = { ...notify, [channel]: on }
    setNotify(next)
    setMessage('')
    save.mutate(
      {
        firstName: customer.firstName,
        lastName: customer.lastName,
        gender: customer.gender || 'male',
        lang: language,
        birth: '',
        ...next,
      },
      {
        onError: (error) => {
          // Сервер не принял — тумблер возвращается, чтобы не врать о настройке.
          setNotify(before)
          setMessage(failureText(error, t))
        },
      },
    )
  }

  const row = (channel: Channel, title: string, hint: string) => (
    <label className="acc-toggle">
      <div>
        <div className="t1">{title}</div>
        <div className="t2">{hint}</div>
      </div>
      <span className="switch">
        <input
          type="checkbox"
          role="switch"
          aria-label={title}
          checked={notify[channel]}
          onChange={(event) => {
            toggle(channel, event.target.checked)
          }}
        />
        <span className="tr2" />
        <span className="th" />
      </span>
    </label>
  )

  return (
    <div className="acc-sec">
      <div className="acc-sh">
        <h3>{t('account:settings.notifications')}</h3>
      </div>
      {row('notifyPush', t('account:edit.push'), t('account:edit.pushHint'))}
      {message ? (
        <p className="acc-note acc-note-err" role="status">
          {message}
        </p>
      ) : null}
    </div>
  )
}
